import { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import { useApp } from "../../store.jsx";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges.js";
import { Icon, Avatar, SkeletonCard } from "../../components/Common.jsx";
import TwoFactorSettings from "../../components/TwoFactorSettings.jsx";
import ReferralCard from "../../components/ReferralCard.jsx";
import { fmt, avatarColor } from "../../data.js";
import { useSession, changePassword, changeEmail, updateUser as authUpdateUser } from "../../lib/auth-client";
import {
  getMe,
  updateMe,
  updateCompany,
  getSmtp,
  saveSmtp,
  testSmtp,
  getAnalytics,
  getKri,
  downloadAnalyticsExport,
  getReminderSettings,
  updateReminderSettings,
  listDepartments,
  getPlans,
  getInvoices,
  createSubscription,
  verifyPayment,
  cancelSubscription,
  upgradeSubscription,
  createCustomSubscription,
  upgradeCustomSubscription,
  getWhatsappDashboard,
  sendWhatsappTest,
  requestPhoneChange,
  getPhoneChangeStatus,
  resendPhoneChangeLink,
  cancelPhoneChange,
} from "../../lib/api";
import WhatsAppVerifyStep from "../../components/WhatsAppVerifyStep.jsx";
import { openCheckout } from "../../lib/razorpay";
import { useRole } from "../../hooks/useRole";
import {
  NewDepartmentModal,
  EditDepartmentModal,
  ConfirmDeleteDepartment,
} from "../../components/Modals.jsx";
import InvoiceDetailModal from "../../components/billing/InvoiceDetailModal.jsx";

const cssVar = (n) =>
  getComputedStyle(document.documentElement).getPropertyValue(n).trim();

// ============ DEPARTMENTS ============
export function Departments() {
  const { goPage, toast, openModal, closeModal } = useApp();
  const role = useRole();
  const canManage = role === "owner" || role === "admin";

  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);

  const refresh = () => {
    setLoading(true);
    listDepartments()
      .then(setDepts)
      .catch(() => toast("Failed to load departments", "error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () =>
    openModal(
      <NewDepartmentModal
        onClose={closeModal}
        onCreated={(d) => {
          setDepts((cur) => [d, ...cur]);
          toast("Department created", "success");
        }}
      />,
    );

  const openEdit = (dept, focus) => {
    setOpenMenuId(null);
    openModal(
      <EditDepartmentModal
        dept={dept}
        focus={focus}
        onClose={closeModal}
        onSaved={(updated) => {
          setDepts((cur) =>
            cur.map((x) => (x.id === updated.id ? updated : x)),
          );
          toast("Department updated", "success");
        }}
      />,
    );
  };

  const openDelete = (dept) => {
    setOpenMenuId(null);
    openModal(
      <ConfirmDeleteDepartment
        dept={dept}
        onClose={closeModal}
        onDeleted={() => {
          setDepts((cur) => cur.filter((x) => x.id !== dept.id));
          toast("Department deleted", "success");
        }}
      />,
    );
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Departments</h1>
          <p className="page-sub">
            Organise your team into {depts.length} department
            {depts.length === 1 ? "" : "s"}
          </p>
        </div>
        {canManage && (
          <button className="btn btn-primary btn-sm" onClick={openNew}>
            <Icon name="plus" size={16} /> New Department
          </button>
        )}
      </div>
      {loading ? (
        <p style={{ color: "var(--text-muted)" }}>Loading departments…</p>
      ) : depts.length === 0 ? (
        <div className="empty-state">
          <div className="ico">
            <Icon name="building" size={28} />
          </div>
          <h3>No departments yet</h3>
          <p>
            {canManage
              ? "Click \"New Department\" to create your first one."
              : "Ask your admin to create the first department."}
          </p>
        </div>
      ) : (
        <div className="grid-3">
          {depts.map((d) => (
            <div
              key={d.id}
              className="card"
              style={{ display: "flex", flexDirection: "column", gap: 14 }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <div
                    className="avatar lg"
                    style={{
                      background: `linear-gradient(135deg,${avatarColor((d.id.length + d.name.length) % 8)},#7C3AED)`,
                    }}
                  >
                    <Icon name="building" size={20} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: 16 }}>{d.name}</h3>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      Manager: {d.manager?.name || "—"}
                    </div>
                  </div>
                </div>
                {canManage && (
                  <div style={{ position: "relative" }}>
                    <button
                      className="icon-btn"
                      onClick={() =>
                        setOpenMenuId(openMenuId === d.id ? null : d.id)
                      }
                    >
                      <Icon name="more-horizontal" size={16} />
                    </button>
                    {openMenuId === d.id && (
                      <div
                        onMouseLeave={() => setOpenMenuId(null)}
                        style={{
                          position: "absolute",
                          top: "100%",
                          right: 0,
                          marginTop: 6,
                          background: "var(--surface)",
                          border: "1px solid var(--border)",
                          borderRadius: 8,
                          boxShadow: "0 8px 24px rgba(0,0,0,.2)",
                          minWidth: 180,
                          zIndex: 20,
                          padding: 4,
                        }}
                      >
                        <button
                          className="side-link"
                          onClick={() => openEdit(d, "rename")}
                        >
                          <Icon name="edit-2" size={14} /> Rename
                        </button>
                        <button
                          className="side-link"
                          onClick={() => openEdit(d, "manager")}
                        >
                          <Icon name="user-cog" size={14} /> Reassign manager
                        </button>
                        <button
                          className="side-link"
                          style={{ color: "var(--error)" }}
                          onClick={() => openDelete(d)}
                        >
                          <Icon name="trash-2" size={14} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    padding: 10,
                    background: "var(--surface-2)",
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                    }}
                  >
                    Employees
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginTop: 2 }}>
                    {d.employeeCount || 0}
                  </div>
                </div>
              </div>
              <button
                className="btn btn-ghost btn-sm"
                style={{ justifyContent: "center" }}
                onClick={() => goPage("employees", { department: d.name })}
              >
                <Icon name="users" size={14} /> View team
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ============ ANALYTICS ============
const RANGE_OPTIONS = [
  { value: "30d", label: "Last 30 days" },
  { value: "7d", label: "Last 7 days" },
  { value: "month", label: "This month" },
  { value: "quarter", label: "Last quarter" },
];

const KRI_TONES = {
  ok: { bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.45)", color: "#10B981", label: "OK" },
  warn: { bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.45)", color: "#F59E0B", label: "Warn" },
  breach: { bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.45)", color: "#F87171", label: "Breach" },
};

function KriPill({ status }) {
  const t = KRI_TONES[status] || KRI_TONES.ok;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        background: t.bg,
        border: `1px solid ${t.border}`,
        color: t.color,
      }}
    >
      {t.label}
    </span>
  );
}

function KriCard({ kris, openKey, onToggle }) {
  return (
    <div className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Risk indicators</div>
          <div className="card-subtitle">
            Click a row to see what needs attention
          </div>
        </div>
      </div>
      <div style={{ padding: "4px 0" }}>
        {kris.length === 0 && (
          <div style={{ padding: 16, color: "var(--text-muted)", fontSize: 13 }}>
            No risk indicators available.
          </div>
        )}
        {kris.map((k) => {
          const t = KRI_TONES[k.status] || KRI_TONES.ok;
          const open = openKey === k.key;
          return (
            <div
              key={k.key}
              style={{
                borderTop: "1px solid var(--border)",
                padding: "12px 4px",
              }}
            >
              <button
                type="button"
                onClick={() => onToggle(open ? null : k.key)}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  textAlign: "left",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <KriPill status={k.status} />
                  <div>
                    <div style={{ fontWeight: 600, color: "var(--text)" }}>
                      {k.label}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {k.description}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 18, color: t.color }}>
                    {k.value}
                  </div>
                  <Icon name={open ? "chevron-up" : "chevron-down"} size={16} />
                </div>
              </button>
              {open && k.items && k.items.length > 0 && (
                <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
                  {k.items.map((it) => {
                    const it_t = KRI_TONES[it.severity] || t;
                    return (
                      <div
                        key={it.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px 10px",
                          borderRadius: 8,
                          background: "var(--surface-2, rgba(255,255,255,0.02))",
                          border: "1px solid var(--border)",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
                            {it.name}
                            {it.department && (
                              <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>
                                {" · "}{it.department}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {it.detail}
                          </div>
                        </div>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 999,
                            color: it_t.color,
                            border: `1px solid ${it_t.border}`,
                            background: it_t.bg,
                            textTransform: "uppercase",
                          }}
                        >
                          {it_t.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {open && (!k.items || k.items.length === 0) && (
                <div style={{ marginTop: 8, padding: "8px 10px", fontSize: 12, color: "var(--text-muted)" }}>
                  Nothing to flag — all clear.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Analytics() {
  const { toast } = useApp();
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [kris, setKris] = useState([]);
  const [openKri, setOpenKri] = useState(null);

  const c1 = useRef(null),
    c2 = useRef(null),
    c3 = useRef(null);
  const ch1 = useRef(null),
    ch2 = useRef(null),
    ch3 = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAnalytics(range)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) toast(e.message || "Could not load analytics", "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range, toast]);

  useEffect(() => {
    let cancelled = false;
    getKri()
      .then((d) => {
        if (!cancelled) setKris(d?.kris || []);
      })
      .catch(() => {
        if (!cancelled) setKris([]);
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  useEffect(() => {
    if (!data) return;
    const tick = cssVar("--tick-color") || "#6B7280";
    const grid = cssVar("--grid-line") || "rgba(38,38,54,.6)";
    const muted = cssVar("--text-muted") || "#94A3B8";
    const opts = (legend, stacked) => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: legend
          ? {
              position: "top",
              align: "end",
              labels: {
                color: muted,
                boxWidth: 10,
                padding: 14,
                font: { size: 12 },
              },
            }
          : { display: false },
        tooltip: {
          backgroundColor: cssVar("--tooltip-bg"),
          borderColor: cssVar("--border"),
          borderWidth: 1,
          padding: 12,
        },
      },
      scales: {
        x: {
          stacked: !!stacked,
          grid: { display: false },
          ticks: { color: tick, font: { size: 11 } },
        },
        y: {
          stacked: !!stacked,
          grid: { color: grid },
          ticks: { color: tick, font: { size: 11 } },
          beginAtZero: true,
        },
      },
    });

    // Daily trend
    const trendLabels = data.dailyTrend.map((r) =>
      new Date(r.date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      }),
    );
    if (ch1.current) ch1.current.destroy();
    ch1.current = new Chart(c1.current, {
      type: "line",
      data: {
        labels: trendLabels,
        datasets: [
          {
            label: "Submitted",
            data: data.dailyTrend.map((r) => r.submitted),
            borderColor: "#8B5CF6",
            backgroundColor: "rgba(139,92,246,.15)",
            fill: true,
            tension: 0.4,
            borderWidth: 2.5,
            pointRadius: 0,
          },
          {
            label: "Expected",
            data: data.dailyTrend.map((r) => r.expected),
            borderColor: "#3B82F6",
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            pointRadius: 0,
            borderDash: [4, 4],
          },
        ],
      },
      options: opts(true),
    });

    // Department compliance
    if (ch2.current) ch2.current.destroy();
    ch2.current = new Chart(c2.current, {
      type: "bar",
      data: {
        labels: data.departmentCompliance.map((x) => x.department),
        datasets: [
          {
            label: "Compliance %",
            data: data.departmentCompliance.map((x) => x.compliance),
            backgroundColor: "#8B5CF6",
            borderRadius: 6,
            maxBarThickness: 24,
          },
        ],
      },
      options: opts(),
    });

    // Task status by day (stacked)
    const tsLabels = data.taskStatusWeek.map((r) =>
      new Date(r.date).toLocaleDateString("en-GB", {
        weekday: "short",
      }),
    );
    if (ch3.current) ch3.current.destroy();
    ch3.current = new Chart(c3.current, {
      type: "bar",
      data: {
        labels: tsLabels,
        datasets: [
          {
            label: "Done",
            data: data.taskStatusWeek.map((r) => r.done),
            backgroundColor: "#10B981",
            borderRadius: 4,
            maxBarThickness: 32,
          },
          {
            label: "In Process",
            data: data.taskStatusWeek.map((r) => r.in_process),
            backgroundColor: "#A78BFA",
            borderRadius: 4,
            maxBarThickness: 32,
          },
          {
            label: "Pending",
            data: data.taskStatusWeek.map((r) => r.pending),
            backgroundColor: "#F59E0B",
            borderRadius: 4,
            maxBarThickness: 32,
          },
        ],
      },
      options: opts(true, true),
    });

    return () => {
      ch1.current?.destroy();
      ch2.current?.destroy();
      ch3.current?.destroy();
    };
  }, [data]);

  const onExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await downloadAnalyticsExport(range);
      toast("Analytics CSV downloaded", "success");
    } catch (e) {
      toast(e.message || "Export failed", "error");
    } finally {
      setExporting(false);
    }
  };

  const kpis = data?.kpis || {
    totalReports: 0,
    avgCompliance: 0,
    blockersRaised: 0,
    activeEmployees: 0,
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Analytics</h1>
          <p className="page-sub">
            Deep insights across your reporting workflow
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <select
            className="select"
            style={{ width: "auto" }}
            value={range}
            onChange={(e) => setRange(e.target.value)}
          >
            {RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            className="btn btn-primary btn-sm"
            onClick={onExport}
            disabled={exporting || loading}
          >
            <Icon name="download" size={16} />{" "}
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>
      <div className="stats-row">
        <div className="stat-card">
          <div className="top">
            <div>
              <div className="label">Total Reports</div>
            </div>
            <div className="icon">
              <Icon name="file-text" size={18} />
            </div>
          </div>
          <div className="value">{fmt.num(kpis.totalReports)}</div>
          <div className="delta">
            <span style={{ color: "var(--text-muted)", fontWeight: 500 }}>
              In selected range
            </span>
          </div>
        </div>
        <div className="stat-card">
          <div className="top">
            <div>
              <div className="label">Avg Compliance</div>
            </div>
            <div className="icon green">
              <Icon name="shield-check" size={18} />
            </div>
          </div>
          <div className="value">{kpis.avgCompliance}%</div>
          <div className="delta">
            <span style={{ color: "var(--text-muted)", fontWeight: 500 }}>
              Avg days submitted / range
            </span>
          </div>
        </div>
        <div className="stat-card">
          <div className="top">
            <div>
              <div className="label">Blockers Raised</div>
            </div>
            <div className="icon amber">
              <Icon name="alert-triangle" size={18} />
            </div>
          </div>
          <div className="value">{fmt.num(kpis.blockersRaised)}</div>
          <div className="delta">
            <span style={{ color: "var(--text-muted)", fontWeight: 500 }}>
              Pending tasks in range
            </span>
          </div>
        </div>
        <div className="stat-card">
          <div className="top">
            <div>
              <div className="label">Active Employees</div>
            </div>
            <div className="icon blue">
              <Icon name="users" size={18} />
            </div>
          </div>
          <div className="value">{fmt.num(kpis.activeEmployees)}</div>
          <div className="delta">
            <span style={{ color: "var(--text-muted)", fontWeight: 500 }}>
              In your scope
            </span>
          </div>
        </div>
      </div>
      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Daily submissions trend</div>
              <div className="card-subtitle">
                Reports vs expected · {data?.range?.days || 0} days
              </div>
            </div>
          </div>
          <div className="chart-wrap">
            <canvas ref={c1} />
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">By department</div>
              <div className="card-subtitle">Compliance %</div>
            </div>
          </div>
          <div className="chart-wrap">
            <canvas ref={c2} />
          </div>
        </div>
      </div>
      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Task status this week</div>
              <div className="card-subtitle">
                Done / In Process / Pending per day
              </div>
            </div>
          </div>
          <div className="chart-wrap">
            <canvas ref={c3} />
          </div>
        </div>
        <KriCard kris={kris} openKey={openKri} onToggle={setOpenKri} />

      </div>
    </section>
  );
}

// ============ WHATSAPP ============
export function WhatsApp() {
  const { toast } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const trendRef = useRef(null);
  const trendChart = useRef(null);

  const load = async () => {
    try {
      const d = await getWhatsappDashboard();
      setData(d);
    } catch (e) {
      toast(e.message || "Could not load WhatsApp data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh live sessions every 10s
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stacked bar chart: WhatsApp vs Web reports per day
  useEffect(() => {
    if (!trendRef.current || !data?.trend30d) return;
    if (trendChart.current) trendChart.current.destroy();
    const trend = data.trend30d;
    const labels = trend.map((d) =>
      new Date(d.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
    );
    trendChart.current = new Chart(trendRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "WhatsApp",
            data: trend.map((d) => d.whatsapp),
            backgroundColor: "#25D366",
            stack: "reports",
            borderRadius: 3,
          },
          {
            label: "Web",
            data: trend.map((d) => d.web),
            backgroundColor: "rgba(139,92,246,.7)",
            stack: "reports",
            borderRadius: 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top",
            align: "end",
            labels: { color: cssVar("--text-muted") || "#94A3B8", boxWidth: 10, font: { size: 11 } },
          },
          tooltip: { mode: "index", intersect: false },
        },
        scales: {
          x: { stacked: true, grid: { display: false }, ticks: { color: cssVar("--tick-color") || "#6B7280", font: { size: 10 } } },
          y: { stacked: true, beginAtZero: true, grid: { color: cssVar("--grid-line") || "rgba(38,38,54,.6)" }, ticks: { color: cssVar("--tick-color") || "#6B7280", font: { size: 10 } } },
        },
      },
    });
    return () => trendChart.current?.destroy();
  }, [data]);

  const copy = (txt) => {
    if (!txt) return;
    navigator.clipboard?.writeText(txt);
    toast("Copied to clipboard", "success");
  };

  const sendTest = async () => {
    if (testing) return;
    setTesting(true);
    try {
      const r = await sendWhatsappTest();
      toast(`Test message sent to ${r.sentTo} — check WhatsApp.`, "success");
    } catch (e) {
      toast(e.message || "Test send failed", "error");
    } finally {
      setTesting(false);
    }
  };

  if (loading || !data) {
    return (
      <section>
        <div className="card" style={{ padding: 24 }}>Loading WhatsApp data…</div>
      </section>
    );
  }

  const { bot, adoption, reportsToday, liveSessions, channelImpact } = data;

  const bentoCard = {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 14,
    padding: 16,
  };
  const smallLabel = {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    color: "var(--text-muted)",
    fontWeight: 600,
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">WhatsApp Bot</h1>
          <p className="page-sub">
            Receive structured daily reports directly through WhatsApp
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            className="btn btn-primary btn-sm"
            onClick={sendTest}
            disabled={testing}
          >
            <Icon name="send" size={16} /> {testing ? "Sending…" : "Send Test"}
          </button>
        </div>
      </div>

      {/* Row 1: Bot info card + 3 stat cards */}
      <div className="wa-stat-grid">
        {/* Bot number card */}
        <div style={{ ...bentoCard, display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={smallLabel}>Bot Phone Number</span>
            <span className="badge badge-success">
              <span className="badge-dot" /> Live
            </span>
          </div>
          {bot.configured ? (
            <>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "monospace" }}>
                {bot.phone}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => copy(bot.phone)}>
                  <Icon name="copy" size={14} /> Copy
                </button>
                {bot.waLink && (
                  <a
                    className="btn btn-ghost btn-sm"
                    href={bot.waLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon name="external-link" size={14} /> Open chat
                  </a>
                )}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
              Bot phone not configured. Set <code>WASIMPLE_BOT_PHONE</code> in
              <code>.env</code>.
            </div>
          )}
        </div>

        {/* Connected */}
        <div style={bentoCard}>
          <div style={smallLabel}>Connected</div>
          <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>
            {adoption.connected}{" "}
            <span style={{ fontSize: 14, color: "var(--text-muted)", fontWeight: 500 }}>
              / {adoption.totalEmployees}
            </span>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {adoption.pct}% of employees
          </div>
        </div>

        {/* Reports today */}
        <div style={bentoCard}>
          <div style={smallLabel}>WA Reports Today</div>
          <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>
            {reportsToday.whatsapp}
            <span style={{ fontSize: 14, color: "var(--text-muted)", fontWeight: 500 }}>
              {" "}/ {reportsToday.total}
            </span>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {reportsToday.whatsappPct}% via WhatsApp
          </div>
        </div>

        {/* Adoption % */}
        <div style={bentoCard}>
          <div style={smallLabel}>Adoption</div>
          <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4, color: "#25D366" }}>
            {adoption.pct}%
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            {adoption.totalEmployees - adoption.connected} employee
            {adoption.totalEmployees - adoption.connected === 1 ? "" : "s"} not yet connected
          </div>
        </div>
      </div>

      {/* Row 2: Live conversations */}
      <div style={{ ...bentoCard, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: liveSessions.length ? "#25D366" : "var(--text-muted)",
                  boxShadow: liveSessions.length ? "0 0 0 4px rgba(37,211,102,.18)" : "none",
                }}
              />
              Live conversations
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {liveSessions.length} employee{liveSessions.length === 1 ? "" : "s"} mid-flow ·
              auto-refreshes every 10s
            </div>
          </div>
        </div>
        {liveSessions.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
            No active conversations right now.
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              maxHeight: 204,
              overflowY: liveSessions.length > 3 ? "auto" : "visible",
              paddingRight: liveSessions.length > 3 ? 4 : 0,
            }}
          >
            {liveSessions.map((s) => (
              <div
                key={s.userId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  borderRadius: 8,
                  background: s.stale ? "rgba(245,158,11,.08)" : "var(--surface-2)",
                  border: s.stale ? "1px solid rgba(245,158,11,.3)" : "1px solid transparent",
                  flexShrink: 0,
                }}
              >
                <Avatar name={s.name} size="sm" seed={s.userId.length} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</div>
                  <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                    {s.stateLabel}
                    {s.taskTotal > 0 && s.state === "awaiting_task_status" && (
                      <>
                        {" · Task "}
                        {s.taskIndex + 1} of {s.taskTotal}
                      </>
                    )}
                    {s.department && <> · {s.department}</>}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: s.stale ? "#f59e0b" : "var(--text-muted)",
                    }}
                  >
                    {s.ageSeconds < 60
                      ? `${s.ageSeconds}s`
                      : s.ageSeconds < 3600
                        ? `${Math.round(s.ageSeconds / 60)}m`
                        : `${Math.round(s.ageSeconds / 3600)}h`}
                  </div>
                  {s.stale && (
                    <div style={{ fontSize: 10, color: "#f59e0b" }}>
                      <Icon name="alert-triangle" size={10} /> stalled
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Row 3: Trend chart */}
      <div style={{ ...bentoCard, marginBottom: 16 }}>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Submission channels — last 30 days</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            WhatsApp vs Web, stacked by day
          </div>
        </div>
        <div style={{ height: 220 }}>
          <canvas ref={trendRef} />
        </div>
      </div>

      {/* Row 4: Channel impact */}
      <div style={bentoCard}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Channel impact on compliance</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            30-day submission rate by primary channel
          </div>
        </div>

        <ChannelBar
          label="WhatsApp users"
          value={channelImpact.whatsappUsersCompliance}
          color="#25D366"
          count={channelImpact.whatsappUserCount}
        />
        <ChannelBar
          label="Web-only users"
          value={channelImpact.webOnlyUsersCompliance}
          color="rgba(139,92,246,.7)"
          count={channelImpact.webOnlyUserCount}
        />

        {channelImpact.whatsappUserCount > 0 && channelImpact.webOnlyUserCount > 0 && (
          <div
            style={{
              marginTop: 14,
              padding: 12,
              borderRadius: 10,
              background: "rgba(37,211,102,.08)",
              border: "1px solid rgba(37,211,102,.2)",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Icon name="trending-up" size={16} />
            <span>
              Employees using WhatsApp submit reports{" "}
              <strong>
                {channelImpact.diff >= 0 ? `${channelImpact.diff}%` : `${-channelImpact.diff}% less`}
              </strong>{" "}
              {channelImpact.diff >= 0 ? "more often" : ""} than web-only users.
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

function ChannelBar({ label, value, color, count }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12.5 }}>
        <span style={{ fontWeight: 600 }}>
          {label}{" "}
          <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>({count})</span>
        </span>
        <span style={{ color: "var(--text-muted)" }}>{value}% avg compliance</span>
      </div>
      <div
        style={{
          height: 8,
          borderRadius: 4,
          background: "var(--surface-3)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${value}%`,
            height: "100%",
            background: color,
            transition: "width 0.6s ease",
          }}
        />
      </div>
    </div>
  );
}


// ============ REMINDERS / TEMPLATES / BILLING / SETTINGS ============
// Sidebar already restricts this page to owner/admin (AppShell.jsx). The
// backend also enforces it. So no extra in-page role check is needed.
export function Reminders() {
  const { toast } = useApp();

  const [form, setForm] = useState({
    morningTime: "09:00",
    eveningTime: "18:00",
    timezone: "Asia/Kolkata",
    enabled: true,
    eveningTemplate: "",
    morningPromptTemplate: "",
    morningYesTemplate: "",
    morningSkipTemplate: "",
    eveningSkippedTemplate: "",
    taskReminderTemplate: "",
    greetingFallbackTemplate: "",
    projectLinkPromptTemplate: "",
    projectLinkedTemplate: "",
    projectLinkSkippedTemplate: "",
    managerNudgeTemplate: "",
    // Announcement settings
    includeInWhatsAppReminders: true,
    includeInEmailReminders: true,
    sendImmediatelyWhenPublished: false,
    showPinnedOnDashboard: true,
    showPinnedInEmployeePortal: true,
    enableExpiry: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  useUnsavedChanges(dirty);
  const TABS = ["Reminder schedule", "Reminder templates", "Announcement Settings"];
  const [active, setActive] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getReminderSettings()
      .then((d) => {
        if (!cancelled) setForm((f) => ({ ...f, ...d }));
      })
      .catch(() => toast("Failed to load reminder settings", "error"))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const next = await updateReminderSettings(form);
      setForm((f) => ({ ...f, ...next }));
      setDirty(false);
      toast("Reminder settings saved", "success");
    } catch (e) {
      toast(e.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section>
        <p style={{ color: "var(--text-muted)" }}>Loading…</p>
      </section>
    );
  }

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Reminders</h1>
          <p className="page-sub">
            Configure the morning check-in &amp; evening reporting WhatsApp reminders
          </p>
        </div>
        <button
          className="btn btn-primary btn-sm"
          disabled={!dirty || saving}
          onClick={save}
        >
          <Icon name="save" size={16} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="tabs">
        {TABS.map((t, i) => (
          <div
            key={t}
            className={`tab ${active === i ? "active" : ""}`}
            onClick={() => setActive(i)}
          >
            {t}
          </div>
        ))}
      </div>

      {/* ─── Tab 1: Reminder schedule ─────────────────────────────── */}
      {active === 0 && (
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Reminder schedule</div>
              <div className="card-subtitle">
                Per-company time (timezone: {form.timezone})
              </div>
            </div>
          </div>
          <div className="field">
            <label>Morning reminder time</label>
            <input
              type="time"
              className="input"
              value={form.morningTime}
              onChange={(e) => update("morningTime", e.target.value)}
            />
            <small style={{ color: "var(--text-muted)" }}>
              Employee gets YES/SKIP prompt. After YES, they can add tasks via WhatsApp.
            </small>
          </div>
          <div className="field">
            <label>Evening reporting time</label>
            <input
              type="time"
              className="input"
              value={form.eveningTime}
              onChange={(e) => update("eveningTime", e.target.value)}
            />
            <small style={{ color: "var(--text-muted)" }}>
              Bot starts the task-by-task report flow at this time.
            </small>
          </div>
        </div>
      )}

      {/* ─── Tab 2: Reminder templates (incl. other bot messages) ──── */}
      {active === 1 && (
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">Reminder templates</div>
              <div className="card-subtitle">
                Edit the messages the bot sends. Use {"{name}"} and {"{count}"}.
              </div>
            </div>
          </div>
          <div className="field">
            <label>Morning reminder 🔒 (fixed)</label>
            <textarea
              className="input"
              rows={3}
              value={form.morningPromptTemplate}
              disabled
              readOnly
              style={{
                opacity: 0.55,
                cursor: "not-allowed",
                background: "var(--bg-subtle, #f3f4f6)",
              }}
            />
            <small style={{ color: "var(--text-muted)" }}>
              🔒 The morning reminder is sent as an <b>approved WhatsApp template</b>,
              so its wording is fixed by WhatsApp and can’t be edited here. WhatsApp
              only delivers approved templates to employees who haven’t replied in
              24h+ (e.g. after a weekend) — a normal editable message wouldn’t reach
              them. To change this wording, a new WhatsApp template must be approved.
              The replies below stay fully editable.
            </small>
          </div>
          <div className="field">
            <label>Morning — YES reply</label>
            <textarea
              className="input"
              rows={3}
              value={form.morningYesTemplate}
              onChange={(e) => update("morningYesTemplate", e.target.value)}
            />
            <small style={{ color: "var(--text-muted)" }}>
              Sent after employee replies YES. Employee can then add tasks. Available: {"{name}"}
            </small>
          </div>
          <div className="field">
            <label>Morning — SKIP / leave reply</label>
            <textarea
              className="input"
              rows={2}
              value={form.morningSkipTemplate}
              onChange={(e) => update("morningSkipTemplate", e.target.value)}
            />
            <small style={{ color: "var(--text-muted)" }}>
              Sent when employee skips or takes leave. No evening reminder sent. Available: {"{name}"}
            </small>
          </div>
          <div className="field">
            <label>Evening reminder</label>
            <textarea
              className="input"
              rows={4}
              value={form.eveningTemplate}
              onChange={(e) => update("eveningTemplate", e.target.value)}
            />
            <small style={{ color: "var(--text-muted)" }}>
              Sent at evening time. Starts the task reporting flow. Available: {"{name}"}, {"{count}"}
            </small>
          </div>
          <div className="field">
            <label>Evening — SKIP reply</label>
            <textarea
              className="input"
              rows={2}
              value={form.eveningSkippedTemplate}
              onChange={(e) => update("eveningSkippedTemplate", e.target.value)}
            />
            <small style={{ color: "var(--text-muted)" }}>
              Sent when employee skips evening report. Tasks carry forward. No placeholders.
            </small>
          </div>

          <div className="divider" style={{ margin: "20px 0 4px", borderTop: "1px solid var(--border)" }} />
          <div className="card-subtitle" style={{ marginBottom: 12 }}>
            Other bot messages
          </div>

          <div className="field">
            <label>Priority task reminder</label>
            <textarea
              className="input"
              rows={11}
              value={form.taskReminderTemplate}
              onChange={(e) => update("taskReminderTemplate", e.target.value)}
              style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12.5 }}
            />
            <small style={{ color: "var(--text-muted)" }}>
              Sent each time a high/medium-priority task hits its reminder
              interval. Available:{" "}
              <code>{"{count}"}</code> <code>{"{max}"}</code>{" "}
              <code>{"{priority}"}</code> <code>{"{priorityEmoji}"}</code>{" "}
              <code>{"{title}"}</code> <code>{"{description}"}</code>{" "}
              <code>{"{assignedBy}"}</code>. <strong>{"{description}"}</strong>{" "}
              already includes a leading line break when present (empty
              otherwise), so keep it adjacent to <code>*{"{title}"}*</code> for
              clean spacing.
            </small>
          </div>

          <div className="field">
            <label>Catch-all bot reply</label>
            <textarea
              className="input"
              rows={3}
              value={form.greetingFallbackTemplate}
              onChange={(e) => update("greetingFallbackTemplate", e.target.value)}
            />
            <small style={{ color: "var(--text-muted)" }}>
              Sent when an employee messages the bot outside an active
              morning/evening flow (e.g. saying "hi" or typing free text).
              Available: <code>{"{name}"}</code> — falls back to "there" when
              unknown.
            </small>
          </div>

          <div className="field">
            <label>Manager nudge reminder</label>
            <textarea
              className="input"
              rows={8}
              value={form.managerNudgeTemplate}
              onChange={(e) => update("managerNudgeTemplate", e.target.value)}
              style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12.5 }}
            />
            <small style={{ color: "var(--text-muted)" }}>
              Sent when a manager nudges an employee from the dashboard ("Send
              reminder" / "Remind all pending"). Available:{" "}
              <code>{"{name}"}</code> (employee first name){" "}
              <code>{"{tasks}"}</code> (bullet list of their pending tasks).
            </small>
          </div>

          <div className="divider" style={{ margin: "20px 0 4px", borderTop: "1px solid var(--border)" }} />
          <div className="card-subtitle" style={{ marginBottom: 12 }}>
            Project linking
          </div>

          <div className="field">
            <label>Link prompt</label>
            <textarea
              className="input"
              rows={4}
              value={form.projectLinkPromptTemplate}
              onChange={(e) => update("projectLinkPromptTemplate", e.target.value)}
            />
            <small style={{ color: "var(--text-muted)" }}>
              Asked after an employee adds task(s) and belongs to exactly one
              project. Available: <code>{"{subject}"}</code> (renders as "it" or
              "these N tasks") <code>{"{project}"}</code> (project name).
            </small>
          </div>

          <div className="field">
            <label>Linked confirmation</label>
            <textarea
              className="input"
              rows={2}
              value={form.projectLinkedTemplate}
              onChange={(e) => update("projectLinkedTemplate", e.target.value)}
            />
            <small style={{ color: "var(--text-muted)" }}>
              Sent after a single task is linked to a project. Available:{" "}
              <code>{"{project}"}</code> (project name).
            </small>
          </div>

          <div className="field">
            <label>Link skipped</label>
            <textarea
              className="input"
              rows={2}
              value={form.projectLinkSkippedTemplate}
              onChange={(e) => update("projectLinkSkippedTemplate", e.target.value)}
            />
            <small style={{ color: "var(--text-muted)" }}>
              Sent when the employee skips the link prompt (replies <code>0</code>{" "}
              or <code>skip</code>). No placeholders.
            </small>
          </div>
        </div>
      )}

      {/* ─── Tab 3: Announcement Settings ─────────────────────────── */}
      {active === 2 && (
        <div className="card">
          <div className="card-header" style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div className="ann-hero-icon">
                <Icon name="megaphone" size={22} />
              </div>
              <div>
                <div className="card-title">Announcement Settings</div>
                <div className="card-subtitle">
                  Control how pinned announcements are delivered and displayed
                </div>
              </div>
            </div>
          </div>

          <div className="ann-settings-groups">
            <SettingsGroup label="Delivery">
              <ToggleRow
                icon="message-circle"
                accent="#25D366"
                accentBg="rgba(37,211,102,.16)"
                label="Send via WhatsApp"
                description="When a pinned announcement is published, send it as a standalone WhatsApp message to all active employees & managers — immediately."
                checked={form.includeInWhatsAppReminders}
                onChange={(v) => update("includeInWhatsAppReminders", v)}
              />
              <ToggleRow
                icon="mail"
                label="Send via email"
                description="Email all active employees & managers as soon as a pinned announcement is published."
                checked={form.sendImmediatelyWhenPublished}
                onChange={(v) => update("sendImmediatelyWhenPublished", v)}
              />
            </SettingsGroup>

            <SettingsGroup label="Visibility">
              <ToggleRow
                icon="layout-dashboard"
                label="Show on dashboard"
                description="Display the latest pinned announcement at the top of the dashboard."
                checked={form.showPinnedOnDashboard}
                onChange={(v) => update("showPinnedOnDashboard", v)}
              />
              <ToggleRow
                icon="users"
                label="Show in employee portal"
                description="Employees can see pinned announcements in their task/report view."
                checked={form.showPinnedInEmployeePortal}
                onChange={(v) => update("showPinnedInEmployeePortal", v)}
              />
            </SettingsGroup>

            <SettingsGroup label="Lifecycle">
              <ToggleRow
                icon="timer"
                label="Enable announcement expiry"
                description="Expired announcements are automatically hidden from all views."
                checked={form.enableExpiry}
                onChange={(v) => update("enableExpiry", v)}
              />
            </SettingsGroup>
          </div>
        </div>
      )}
    </section>
  );
}

// Labelled group of related settings rows
function SettingsGroup({ label, children }) {
  return (
    <div className="ann-settings-group">
      <div className="ann-settings-group-label">{label}</div>
      {children}
    </div>
  );
}

// Helper component for toggle rows. The whole card is clickable; the inner
// Toggle stops propagation so a direct click on the switch doesn't double-fire.
function ToggleRow({ icon, label, description, checked, onChange, disabled, accent, accentBg }) {
  const accentVars =
    accent || accentBg
      ? { "--row-accent": accent, "--row-accent-bg": accentBg }
      : undefined;
  return (
    <div
      className={`ann-toggle-row${checked ? " is-on" : ""}${disabled ? " is-disabled" : ""}`}
      style={accentVars}
      onClick={() => !disabled && onChange?.(!checked)}
      role="button"
      aria-pressed={checked}
    >
      {icon && (
        <div className="ann-toggle-icon">
          <Icon name={icon} size={18} />
        </div>
      )}
      <div className="ann-toggle-body">
        <span className="ann-toggle-label">{label}</span>
        {description && <span className="ann-toggle-desc">{description}</span>}
      </div>
      <span onClick={(e) => e.stopPropagation()} style={{ display: "flex" }}>
        <Toggle checked={checked} onChange={onChange} disabled={disabled} />
      </span>
    </div>
  );
}

// Toggle switch component
function Toggle({ checked, onChange, disabled }) {
  return (
    <label
      style={{
        position: "relative",
        display: "inline-block",
        width: 44,
        height: 24,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        flexShrink: 0,
      }}
    >
      <span
        onClick={() => !disabled && onChange?.(!checked)}
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 999,
          background: checked
            ? "linear-gradient(90deg,#4f46e5,#7c3aed)"
            : "var(--border)",
          transition: "background .2s",
        }}
      />
      <span
        style={{
          position: "absolute",
          top: 3,
          left: checked ? 23 : 3,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#fff",
          transition: "left .2s",
          boxShadow: "0 1px 3px rgba(0,0,0,.2)",
        }}
      />
    </label>
  );
}

function CustomPlanCard({
  plan,
  rates,
  min,
  isCurrent,
  isBusy,
  hasSub,
  onSubmit,
  currentOverrides,
}) {
  const safeMin = min || { employees: 5, managers: 5, reports: 200 };
  const safeRates = rates || { perEmployee: 1200, perManager: 7800, perReport: 25 };
  const [employees, setEmployees] = useState(
    currentOverrides?.maxEmployees ?? safeMin.employees,
  );
  const [managers, setManagers] = useState(
    currentOverrides?.maxManagers ?? safeMin.managers,
  );
  const [reports, setReports] = useState(
    currentOverrides?.reportsPerMonth ?? safeMin.reports,
  );

  const e = Number(employees) || 0;
  const m = Number(managers) || 0;
  const r = Number(reports) || 0;
  const belowMin =
    e < safeMin.employees || m < safeMin.managers || r < safeMin.reports;
  const amountPaise =
    e * safeRates.perEmployee + m * safeRates.perManager + r * safeRates.perReport;
  const priceLabel = `₹${(amountPaise / 100).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}`;

  const inputStyle = {
    width: 90,
    padding: "4px 8px",
    border: "1px solid var(--border)",
    borderRadius: 6,
    background: "var(--surface)",
    color: "var(--text)",
    fontSize: 13,
  };
  const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    padding: "6px 0",
  };

  return (
    <div
      className="card"
      style={{
        borderColor: isCurrent ? "rgba(139,92,246,.6)" : "var(--border)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <h3 style={{ marginTop: 0 }}>{plan.name}</h3>
      <div style={{ fontSize: 24, fontWeight: 700 }}>
        {priceLabel}
        <span style={{ fontSize: 13, color: "var(--text-muted)" }}> /mo</span>
      </div>
      <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "6px 0 10px" }}>
        Pick your own limits. Includes all Growth features.
      </p>
      <div style={{ fontSize: 13, flex: 1 }}>
        <div style={rowStyle}>
          <label htmlFor="cp-emp">Employees</label>
          <input
            id="cp-emp"
            type="number"
            min={safeMin.employees}
            value={employees}
            onChange={(ev) => setEmployees(ev.target.value)}
            disabled={isCurrent || isBusy}
            style={inputStyle}
          />
        </div>
        <div style={rowStyle}>
          <label htmlFor="cp-mgr">Managers</label>
          <input
            id="cp-mgr"
            type="number"
            min={safeMin.managers}
            value={managers}
            onChange={(ev) => setManagers(ev.target.value)}
            disabled={isCurrent || isBusy}
            style={inputStyle}
          />
        </div>
        <div style={rowStyle}>
          <label htmlFor="cp-rep">Reports / mo</label>
          <input
            id="cp-rep"
            type="number"
            min={safeMin.reports}
            value={reports}
            onChange={(ev) => setReports(ev.target.value)}
            disabled={isCurrent || isBusy}
            style={inputStyle}
          />
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--text-muted)",
            marginTop: 8,
            lineHeight: 1.5,
          }}
        >
          ₹{safeRates.perEmployee / 100}/employee · ₹{safeRates.perManager / 100}/manager
          · ₹{safeRates.perReport / 100}/report · min {safeMin.employees}e / {safeMin.managers}m / {safeMin.reports}r
        </div>
      </div>
      <button
        className={`btn ${isCurrent ? "btn-ghost" : "btn-primary"}`}
        disabled={isCurrent || isBusy || belowMin || amountPaise <= 0}
        onClick={() =>
          onSubmit({ employees: e, managers: m, reports: r })
        }
        style={{ marginTop: 14, justifyContent: "center" }}
      >
        {isCurrent
          ? "Current Plan"
          : isBusy
            ? "Processing…"
            : belowMin
              ? "Below minimum"
              : hasSub
                ? `Switch — pay ${priceLabel}`
                : `Subscribe — pay ${priceLabel}`}
      </button>
    </div>
  );
}

export function Billing() {
  const { toast, openModal, closeModal } = useApp();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [me, setMe] = useState(null);

  const viewInvoice = (inv) =>
    openModal(
      <InvoiceDetailModal
        invoice={inv}
        company={me?.company}
        onClose={closeModal}
      />,
      "lg",
    );
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [planRes, invRes, meRes] = await Promise.all([
        getPlans(),
        getInvoices().catch(() => ({ invoices: [] })),
        getMe().catch(() => null),
      ]);
      setData(planRes);
      setInvoices(invRes.invoices || []);
      setMe(meRes);
    } catch (e) {
      toast("Failed to load billing info", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectPlan = async (planKey) => {
    setBusyPlan(planKey);
    try {
      const sub = data?.current?.subscription;
      const isUpgrade =
        sub && ["active", "past_due"].includes(sub.status);

      // Confirm before charging the full new-plan amount on switch
      if (isUpgrade) {
        const planMeta = (data?.plans || []).find((p) => p.key === planKey);
        const price = planMeta ? `₹${(planMeta.amount / 100).toLocaleString()}` : "the new plan price";
        if (
          !window.confirm(
            `Switch to ${planKey[0].toUpperCase() + planKey.slice(1)}?\n\n` +
              `You'll be charged ${price} now for the new plan. ` +
              `Your remaining days on the current plan will not be refunded.`,
          )
        ) {
          return;
        }
      }

      // Both first-time subscribe and upgrade hit endpoints that return the
      // same { keyId, razorpaySubscriptionId, amount, currency } shape.
      const created = isUpgrade
        ? await upgradeSubscription(planKey)
        : await createSubscription(planKey);

      const result = await openCheckout({
        keyId: created.keyId,
        subscriptionId: created.razorpaySubscriptionId,
        amount: created.amount,
        currency: created.currency || "INR",
        description: `${planKey[0].toUpperCase() + planKey.slice(1)} Plan — Monthly`,
        prefill: {
          name: me?.user?.name || "",
          email: me?.user?.email || "",
          contact: me?.user?.phone || "",
        },
      });

      await verifyPayment(result);
      toast(
        isUpgrade
          ? `Switched to ${planKey[0].toUpperCase() + planKey.slice(1)}.`
          : "Payment successful! Subscription active.",
        "success",
      );
      await refresh();
    } catch (e) {
      if (e?.reason === "dismissed") {
        toast("Payment cancelled", "info");
      } else if (e?.reason === "failed") {
        toast(e.error?.description || "Payment failed", "error");
      } else {
        toast(e.message || "Something went wrong", "error");
      }
    } finally {
      setBusyPlan(null);
    }
  };

  const handleCustomCheckout = async ({ employees, managers, reports }) => {
    setBusyPlan("custom");
    try {
      const sub = data?.current?.subscription;
      const isUpgrade = sub && ["active", "past_due"].includes(sub.status);

      const payload = { employees, managers, reports };
      const created = isUpgrade
        ? await upgradeCustomSubscription(payload)
        : await createCustomSubscription(payload);

      if (isUpgrade) {
        const ok = window.confirm(
          `Switch to Custom Plan?\n\n` +
            `You'll be charged ₹${(created.amount / 100).toLocaleString()} now for the new plan. ` +
            `Your remaining days on the current plan will not be refunded.`,
        );
        if (!ok) return;
      }

      const result = await openCheckout({
        keyId: created.keyId,
        subscriptionId: created.razorpaySubscriptionId,
        amount: created.amount,
        currency: created.currency || "INR",
        description: "Custom Plan — Monthly",
        prefill: {
          name: me?.user?.name || "",
          email: me?.user?.email || "",
          contact: me?.user?.phone || "",
        },
      });

      await verifyPayment(result);
      toast(
        isUpgrade
          ? "Switched to Custom Plan."
          : "Payment successful! Subscription active.",
        "success",
      );
      await refresh();
    } catch (e) {
      if (e?.reason === "dismissed") {
        toast("Payment cancelled", "info");
      } else if (e?.reason === "failed") {
        toast(e.error?.description || "Payment failed", "error");
      } else {
        toast(e.message || "Something went wrong", "error");
      }
    } finally {
      setBusyPlan(null);
    }
  };

  const handleCancel = async () => {
    if (
      !window.confirm(
        "Cancel subscription? Access continues until the end of the current billing period.",
      )
    )
      return;
    try {
      const res = await cancelSubscription();
      const endDate = res?.currentPeriodEnd
        ? new Date(res.currentPeriodEnd).toLocaleDateString()
        : null;
      toast(
        endDate
          ? `Subscription cancelled. Access until ${endDate}.`
          : "Subscription cancelled.",
        "success",
      );
      await refresh();
    } catch (e) {
      toast(e.message || "Cancel failed", "error");
    }
  };

  if (loading) {
    return (
      <section>
        <SkeletonCard lines={6} />
      </section>
    );
  }

  const { plans = [], current = {}, customConfig } = data || {};
  const currentPlanKey = current.plan;
  const trial = current.trial;
  const sub = current.subscription;
  const prettyPlan = (k) =>
    !k
      ? "—"
      : k === "free_trial"
        ? "Free Trial"
        : k === "custom"
          ? "Custom Plan"
          : k[0].toUpperCase() + k.slice(1);
  const limitLabel = (v) => {
    if (v === null || v === undefined || v === Infinity) return "Unlimited";
    if (typeof v === "number") return v > 99999 ? "Unlimited" : v.toLocaleString();
    return v;
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Billing & Plan</h1>
          <p className="page-sub">Manage your subscription</p>
        </div>
      </div>

      {/* Current plan summary */}
      <div
        className="card"
        style={{
          background:
            "linear-gradient(135deg,rgba(139,92,246,.15),var(--surface))",
          borderColor: "rgba(139,92,246,.3)",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <span className="badge badge-primary">Current Plan</span>
            <h3 style={{ fontSize: 26, marginTop: 6, marginBottom: 4 }}>
              {prettyPlan(currentPlanKey)}
            </h3>
            <div style={{ color: "var(--text-muted)", fontSize: 14 }}>
              {trial &&
                `${trial.remainingDays} day${trial.remainingDays === 1 ? "" : "s"} of trial remaining`}
              {sub &&
                (sub.status === "canceled"
                  ? `Cancelled · Access until ${new Date(sub.currentPeriodEnd).toLocaleDateString()}`
                  : sub.status === "past_due"
                    ? `Payment failed · Update payment method`
                    : sub.status === "active"
                      ? `Active · Renews on ${new Date(sub.currentPeriodEnd).toLocaleDateString()}`
                      : `Status: ${sub.status}`)}
              {!trial && !sub && current.isActive === false && (
                <span style={{ color: "#dc2626" }}>
                  Trial expired — choose a plan to continue
                </span>
              )}
            </div>
            {sub?.lastChangedBy && (
              <div
                style={{
                  marginTop: 12,
                  padding: "8px 12px",
                  background: "rgba(139,92,246,0.08)",
                  border: "1px solid rgba(139,92,246,0.2)",
                  borderRadius: 10,
                  fontSize: 12,
                  color: "var(--text-muted)",
                }}
              >
                <strong style={{ color: "var(--text)" }}>
                  {sub.lastChangedBy.source === "admin"
                    ? "Plan set by platform admin"
                    : "Plan selected by"}
                </strong>{" "}
                {sub.lastChangedBy.name || sub.lastChangedBy.email}
                {sub.lastChangedBy.at && (
                  <>
                    {" "}· {new Date(sub.lastChangedBy.at).toLocaleDateString()}
                  </>
                )}
              </div>
            )}
          </div>
          {sub && sub.status === "active" && (
            <button className="btn btn-ghost btn-sm" onClick={handleCancel}>
              Cancel subscription
            </button>
          )}
        </div>
      </div>

      {/* Plans grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
          gap: 16,
          marginBottom: 20,
        }}
      >
        {plans.map((p) => {
          const isCurrent = currentPlanKey === p.key;
          const isBusy = busyPlan === p.key;
          const f = p.features || {};
          if (p.key === "custom") {
            return (
              <CustomPlanCard
                key={p.key}
                plan={p}
                rates={customConfig?.rates}
                min={customConfig?.min}
                isCurrent={isCurrent}
                isBusy={isBusy}
                hasSub={!!sub}
                onSubmit={handleCustomCheckout}
                currentOverrides={current.featureOverrides || sub?.featureOverrides}
              />
            );
          }
          return (
            <div
              key={p.key}
              className="card"
              style={{
                borderColor: isCurrent
                  ? "rgba(139,92,246,.6)"
                  : "var(--border)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <h3 style={{ marginTop: 0, textTransform: "capitalize" }}>
                {p.name}
              </h3>
              <div style={{ fontSize: 24, fontWeight: 700 }}>
                {p.amount === 0
                  ? "Custom"
                  : `₹${(p.amount / 100).toLocaleString()}`}
                {p.amount !== 0 && (
                  <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
                    {" "}
                    /mo
                  </span>
                )}
              </div>
              <ul
                style={{
                  fontSize: 13,
                  color: "var(--text-muted)",
                  paddingLeft: 18,
                  marginTop: 12,
                  flex: 1,
                }}
              >
                <li>Up to {limitLabel(f.maxEmployees)} employees</li>
                <li>Up to {limitLabel(f.maxManagers)} managers</li>
                <li>{limitLabel(f.reportsPerMonth)} reports/mo</li>
                <li>{limitLabel(f.whatsappNumbers)} WhatsApp number(s)</li>
                <li>AI analysis: {f.aiAnalysis}</li>
                {f.departments && <li>Departments</li>}
                {f.customTemplates && <li>Custom templates</li>}
                {f.slackIntegration && <li>Slack &amp; Sheets integration</li>}
                {f.apiAccess && <li>API access</li>}
                {f.analyticsAdvanced && <li>Advanced analytics</li>}
                {f.customReminders && <li>Custom reminders &amp; shifts</li>}
                {f.sso && <li>SSO &amp; SAML</li>}
              </ul>
              <button
                className={`btn ${isCurrent ? "btn-ghost" : "btn-primary"}`}
                disabled={isCurrent || isBusy}
                onClick={() => handleSelectPlan(p.key)}
                style={{ marginTop: 14, justifyContent: "center" }}
              >
                {isCurrent
                  ? "Current Plan"
                  : isBusy
                    ? "Processing…"
                    : sub
                      ? "Switch to this plan"
                      : "Subscribe"}
              </button>
            </div>
          );
        })}
      </div>

      {/* Invoices */}
      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Invoice History</div>
          </div>
        </div>
        {invoices.length === 0 ? (
          <div
            style={{
              color: "var(--text-muted)",
              fontSize: 14,
              padding: "12px 0",
            }}
          >
            No invoices yet. Invoices will appear here after your first payment.
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Period</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  onClick={() => viewInvoice(inv)}
                  style={{ cursor: "pointer" }}
                  title="View invoice"
                >
                  <td data-label="Date">{fmt.date(new Date(inv.createdAt))}</td>
                  <td data-label="Amount">
                    <strong>
                      ₹{(inv.amount / 100).toLocaleString()}
                    </strong>
                  </td>
                  <td data-label="Period">
                    {new Date(inv.periodStart).toLocaleDateString()} –{" "}
                    {new Date(inv.periodEnd).toLocaleDateString()}
                  </td>
                  <td data-label="Status">
                    <span
                      className={`badge ${inv.status === "paid" ? "badge-success" : "badge-warning"}`}
                    >
                      <span className="badge-dot" /> {inv.status}
                    </span>
                  </td>
                  <td data-label="" style={{ textAlign: "right" }}>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        viewInvoice(inv);
                      }}
                    >
                      <Icon name="eye" size={14} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

export function Settings() {
  const { toast } = useApp();
  const { data: session } = useSession();
  const role = session?.user?.role || "employee";
  // Only the tenant owner manages the organisation. Platform admin (role === "admin")
  // belongs to the seeded __platform__ company and must not edit it from Settings.
  const isOwner = role === "owner";
  const isAdmin = isOwner; // legacy alias used elsewhere in this file

  const tabs = isOwner
    ? ["Profile", "Organisation", "SMTP", "Security"]
    : ["Profile", "Security"];
  const [active, setActive] = useState(0);

  // Profile state
  const [profile, setProfile] = useState({ name: "", email: "", phone: "" });
  const [originalEmail, setOriginalEmail] = useState("");
  const [originalPhone, setOriginalPhone] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  // null when no change in flight; otherwise the response from /api/me/phone/request-change.
  const [phoneChange, setPhoneChange] = useState(null);

  // Organisation state — only company name
  const [orgName, setOrgName] = useState("");
  const [orgSaving, setOrgSaving] = useState(false);

  // SMTP state
  const [smtp, setSmtp] = useState({
    provider: "custom",
    host: "smtp.gmail.com",
    port: 587,
    useSsl: false,
    user: "",
    pass: "",
    fromName: "",
    fromEmail: "",
    appName: "Daily Reports Platform",
    enabled: true,
  });
  const [smtpHasPassword, setSmtpHasPassword] = useState(false);
  const [smtpLoaded, setSmtpLoaded] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [showPass, setShowPass] = useState(false);

  // Password state
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  // Load profile + company name on mount.
  // Platform admin (role === "admin") cannot hit /api/me — populate from session instead.
  useEffect(() => {
    if (role === "admin") {
      const u = session?.user;
      setProfile({
        name: u?.name || "",
        email: u?.email || "",
        phone: u?.phone || "",
      });
      setOriginalEmail(u?.email || "");
      setOriginalPhone(u?.phone || "");
      setEmailVerified(!!u?.emailVerified);
      setPhoneVerified(false);
      return;
    }
    getMe()
      .then(({ user, company }) => {
        setProfile({
          name: user?.name || "",
          email: user?.email || "",
          phone: user?.phone || "",
        });
        setOriginalEmail(user?.email || "");
        setOriginalPhone(user?.phone || "");
        setEmailVerified(!!user?.emailVerified);
        setPhoneVerified(!!user?.phoneVerified);
        setOrgName(company?.name || "");
      })
      .catch(() => {});
  }, [role, session]);

  useEffect(() => {
    if (!isOwner || smtpLoaded) return;
    getSmtp()
      .then(({ smtp: s }) => {
        if (s) {
          setSmtp({
            provider: s.provider || "custom",
            host: s.host || "smtp.gmail.com",
            port: s.port || 587,
            useSsl: s.useSsl ?? false,
            user: s.user || "",
            pass: "",
            fromName: s.fromName || "",
            fromEmail: s.fromEmail || "",
            appName: s.appName || "Daily Reports Platform",
            enabled: s.enabled ?? true,
          });
          setSmtpHasPassword(!!s.hasPassword);
        }
        setSmtpLoaded(true);
      })
      .catch(() => setSmtpLoaded(true));
  }, [isOwner, smtpLoaded]);

  // Provider presets — keep in sync with the backend smtpSchema enum. Selecting a
  // provider auto-fills host/port/TLS; "custom" leaves the fields as-is.
  const SMTP_PROVIDERS = [
    {
      id: "brevo",
      label: "Brevo",
      host: "smtp-relay.brevo.com",
      port: 587,
      useSsl: false,
      hint: "Username is your SMTP login (e.g. xxxx@smtp-brevo.com), not your account email. Password is the generated SMTP key. Sender must be a verified sender in Brevo.",
    },
    {
      id: "gmail",
      label: "Gmail / Google Workspace",
      host: "smtp.gmail.com",
      port: 587,
      useSsl: false,
      hint: "Use a 16-char App Password (not your login password). ~500 emails/day cap.",
    },
    {
      id: "resend",
      label: "Resend (SMTP)",
      host: "smtp.resend.com",
      port: 465,
      useSsl: true,
      hint: 'Username is "resend". Password is your Resend API key. The sender domain must be verified in Resend.',
    },
    {
      id: "custom",
      label: "Custom / other SMTP",
      host: "",
      port: 587,
      useSsl: false,
      hint: "Enter your provider's SMTP host, port and credentials manually.",
    },
  ];
  const smtpProvider =
    SMTP_PROVIDERS.find((p) => p.id === smtp.provider) || SMTP_PROVIDERS[3];

  const pickProvider = (id) => {
    const p = SMTP_PROVIDERS.find((x) => x.id === id) || SMTP_PROVIDERS[3];
    setSmtp((s) => ({
      ...s,
      provider: id,
      ...(id !== "custom"
        ? { host: p.host, port: p.port, useSsl: p.useSsl }
        : {}),
    }));
  };

  const saveSmtpConfig = async () => {
    if (!smtp.host.trim() || !smtp.user.trim()) {
      toast("Host and username are required", "error");
      return;
    }
    if (!smtp.pass && !smtpHasPassword) {
      toast("App password is required", "error");
      return;
    }
    setSmtpSaving(true);
    try {
      await saveSmtp(smtp);
      setSmtpHasPassword(true);
      setSmtp((s) => ({ ...s, pass: "" }));
      toast("SMTP configuration saved", "success");
    } catch (err) {
      toast(err.message || "Could not save SMTP config", "error");
    } finally {
      setSmtpSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setSmtpTesting(true);
    try {
      await testSmtp();
      toast("Test email sent! Check your inbox.", "success");
    } catch (err) {
      toast(err.message || "SMTP test failed", "error");
    } finally {
      setSmtpTesting(false);
    }
  };

  const phoneDigits = (v) => (v || "").replace(/\D/g, "");

  const saveProfile = async () => {
    if (!profile.name.trim() || !profile.email.trim()) {
      toast("Name and email are required", "error");
      return;
    }
    setProfileSaving(true);
    try {
      const newPhoneTrimmed = (profile.phone || "").trim();
      const phoneCleared =
        originalPhone && newPhoneTrimmed === "" && role !== "admin";
      const phoneChanged =
        role !== "admin" &&
        newPhoneTrimmed !== "" &&
        phoneDigits(newPhoneTrimmed) !== phoneDigits(originalPhone);

      // Platform admin uses Better Auth directly; tenant users hit /api/me.
      if (role === "admin") {
        await authUpdateUser({ name: profile.name, phone: profile.phone });
      } else if (phoneCleared) {
        // Clearing a phone doesn't need verification — anyone can remove
        // their own number without claiming someone else's identity.
        await updateMe({ name: profile.name, phone: "" });
        setOriginalPhone("");
        setPhoneVerified(false);
      } else {
        await updateMe({ name: profile.name });
      }

      // Email change — Better Auth sends a verification link; the DB row only
      // switches after the user clicks it.
      if (profile.email.trim() !== originalEmail) {
        const res = await changeEmail({
          newEmail: profile.email.trim(),
          callbackURL: window.location.origin + "/app",
        });
        if (res?.error) {
          toast(res.error.message || "Could not start email change", "error");
        } else {
          toast(
            "Verification link sent to your new email. Click it to confirm the change.",
            "info",
          );
        }
      } else if (!phoneChanged) {
        toast("Profile saved", "success");
      }

      // Phone change — kick off WhatsApp verification. Local profile.phone
      // stays at the new value while waiting; the user row is only updated
      // after the webhook commit.
      if (phoneChanged) {
        try {
          const data = await requestPhoneChange(newPhoneTrimmed);
          setPhoneChange({ ...data, newPhone: newPhoneTrimmed });
        } catch (err) {
          // The api wrapper sets err.message to the backend `error` field.
          if (err.message === "phone_in_use" || err.status === 409) {
            toast(
              "That phone is already linked to another account.",
              "error",
            );
          } else if (err.message === "same_phone") {
            // No-op — number didn't actually change once normalized.
          } else if (err.message === "invalid_phone") {
            toast("Please enter a valid phone number.", "error");
          } else {
            toast(
              err.message || "Could not start phone verification",
              "error",
            );
          }
          // Roll the UI back to the verified phone so it doesn't claim success.
          setProfile((p) => ({ ...p, phone: originalPhone }));
        }
      }
    } catch (err) {
      toast(err.message || "Could not save profile", "error");
    } finally {
      setProfileSaving(false);
    }
  };

  const onPhoneVerified = async () => {
    setPhoneChange(null);
    try {
      const { user } = await getMe();
      setProfile((p) => ({ ...p, phone: user?.phone || "" }));
      setOriginalPhone(user?.phone || "");
      setPhoneVerified(!!user?.phoneVerified);
      toast("Phone number verified and updated", "success");
    } catch {
      toast("Phone updated — refresh to see the new number", "success");
    }
  };

  const closePhoneModal = async () => {
    setPhoneChange(null);
    setProfile((p) => ({ ...p, phone: originalPhone }));
    try {
      await cancelPhoneChange();
    } catch {
      // best-effort cleanup; row will expire naturally
    }
  };

  const saveOrg = async () => {
    if (!orgName.trim()) {
      toast("Organisation name is required", "error");
      return;
    }
    setOrgSaving(true);
    try {
      await updateCompany({ name: orgName });
      toast("Organisation updated", "success");
    } catch (err) {
      toast(err.message || "Could not save organisation", "error");
    } finally {
      setOrgSaving(false);
    }
  };

  const submitPassword = async () => {
    if (pw.next.length < 8) {
      toast("New password must be at least 8 characters", "error");
      return;
    }
    if (pw.next !== pw.confirm) {
      toast("Passwords do not match", "error");
      return;
    }
    setPwSaving(true);
    try {
      const res = await changePassword({
        currentPassword: pw.current,
        newPassword: pw.next,
      });
      if (res?.error) {
        toast(res.error.message || "Could not change password", "error");
      } else {
        toast("Password updated", "success");
        setPw({ current: "", next: "", confirm: "" });
      }
    } catch (err) {
      toast(err.message || "Could not change password", "error");
    } finally {
      setPwSaving(false);
    }
  };

  const tabLabel = tabs[active];

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-sub">
            Manage your profile{isAdmin ? " and organisation" : ""}.
          </p>
        </div>
      </div>
      <div className="tabs">
        {tabs.map((t, i) => (
          <div
            key={t}
            className={`tab ${active === i ? "active" : ""}`}
            onClick={() => setActive(i)}
          >
            {t}
          </div>
        ))}
      </div>

      {tabLabel === "Profile" && (
        <div className="card">
          <h3 style={{ fontSize: 16, marginBottom: 18 }}>
            Profile information
          </h3>
          <div className="field">
            <label>Full name</label>
            <input
              className="input"
              value={profile.name}
              onChange={(e) =>
                setProfile((p) => ({ ...p, name: e.target.value }))
              }
            />
          </div>
          <div className="field">
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              Email address
              {emailVerified && originalEmail && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#10b981",
                    background: "rgba(16,185,129,.12)",
                    padding: "1px 8px",
                    borderRadius: 999,
                  }}
                >
                  ✓ Verified
                </span>
              )}
            </label>
            <input
              className="input"
              type="email"
              value={profile.email}
              onChange={(e) =>
                setProfile((p) => ({ ...p, email: e.target.value }))
              }
            />
            <small style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
              Changing your email sends a verification link to the new address.
              It only switches after you click the link.
            </small>
          </div>
          <div className="field">
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              Phone
              {phoneVerified && originalPhone && role !== "admin" && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#10b981",
                    background: "rgba(16,185,129,.12)",
                    padding: "1px 8px",
                    borderRadius: 999,
                  }}
                >
                  ✓ Verified
                </span>
              )}
            </label>
            <input
              className="input"
              value={profile.phone}
              onChange={(e) =>
                setProfile((p) => ({ ...p, phone: e.target.value }))
              }
              placeholder="+91 98765 43210"
              disabled={!!phoneChange}
            />
            {role !== "admin" && (
              <small style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                Setting a new phone sends a one-time verification message to
                WhatsApp. The number only switches after you complete the
                verify step.
              </small>
            )}
          </div>
          <button
            className="btn btn-primary"
            onClick={saveProfile}
            disabled={profileSaving || !!phoneChange}
          >
            <Icon name="check" size={16} />{" "}
            {profileSaving ? "Saving…" : "Save changes"}
          </button>

          {phoneChange && (
            <div
              role="dialog"
              aria-modal="true"
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 100,
                padding: 16,
              }}
              onClick={closePhoneModal}
            >
              <div
                className="card"
                onClick={(e) => e.stopPropagation()}
                style={{ width: "100%", maxWidth: 460, position: "relative" }}
              >
                <button
                  type="button"
                  onClick={closePhoneModal}
                  className="btn btn-ghost btn-sm"
                  aria-label="Close"
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                  }}
                >
                  <Icon name="x" size={16} />
                </button>
                <h3 style={{ margin: "0 0 6px", fontSize: 16 }}>
                  Verify your new phone number
                </h3>
                <p
                  style={{
                    color: "var(--text-muted)",
                    fontSize: 13,
                    margin: "0 0 14px",
                  }}
                >
                  Until you complete this step, your old number{" "}
                  {originalPhone ? (
                    <strong style={{ color: "var(--text)" }}>
                      ({originalPhone})
                    </strong>
                  ) : null}{" "}
                  stays active for WhatsApp.
                </p>
                <WhatsAppVerifyStep
                  token={phoneChange.verificationToken}
                  waLink={phoneChange.waLink}
                  botPhone={phoneChange.botPhone}
                  verifyMessage={phoneChange.verifyMessage}
                  phone={phoneChange.newPhone}
                  expiresAt={phoneChange.expiresAt}
                  onVerified={onPhoneVerified}
                  getStatus={getPhoneChangeStatus}
                  doResend={resendPhoneChangeLink}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {tabLabel === "Organisation" && isAdmin && (
        <div className="card">
          <h3 style={{ fontSize: 16, marginBottom: 18 }}>
            Organisation details
          </h3>
          <div className="field">
            <label>Organisation name</label>
            <input
              className="input"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={saveOrg}
            disabled={orgSaving}
          >
            <Icon name="check" size={16} /> {orgSaving ? "Saving…" : "Save"}
          </button>
        </div>
      )}

      {tabLabel === "Organisation" && isAdmin && <ReferralCard />}

      {tabLabel === "SMTP" && isOwner && (
        <div className="card">
          <h3 style={{ fontSize: 16, marginBottom: 6 }}>
            Email (SMTP) Configuration
          </h3>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 18 }}>
            Use your own SMTP server to send emails from your domain. When
            disabled, the platform default is used.
          </p>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <label
              style={{ position: "relative", display: "inline-block", width: 44, height: 24, cursor: "pointer" }}
              onClick={() => setSmtp((s) => ({ ...s, enabled: !s.enabled }))}
            >
              <span
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: 999,
                  background: smtp.enabled
                    ? "linear-gradient(90deg,#4f46e5,#7c3aed)"
                    : "var(--border)",
                  transition: "background .2s",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  top: 3,
                  left: smtp.enabled ? 23 : 3,
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  background: "#fff",
                  transition: "left .2s",
                }}
              />
            </label>
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              {smtp.enabled ? "Custom SMTP enabled" : "Using platform default"}
            </span>
          </div>

          <div className="field">
            <label>Provider</label>
            <select
              className="input"
              value={smtp.provider}
              onChange={(e) => pickProvider(e.target.value)}
            >
              {SMTP_PROVIDERS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <small style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
              {smtpProvider.hint}
            </small>
          </div>

          <div className="field">
            <label>SMTP Host</label>
            <input
              className="input"
              value={smtp.host}
              onChange={(e) =>
                setSmtp((s) => ({ ...s, host: e.target.value }))
              }
              placeholder="smtp.gmail.com"
            />
          </div>

          <div className="field">
            <label>Port</label>
            <input
              className="input"
              type="number"
              value={smtp.port}
              onChange={(e) =>
                setSmtp((s) => ({ ...s, port: Number(e.target.value) }))
              }
              placeholder="587"
              style={{ maxWidth: 140 }}
            />
            <small style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
              587 for TLS (recommended), 465 for SSL
            </small>
          </div>

          <div className="field">
            <label
              style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
            >
              <input
                type="checkbox"
                checked={smtp.useSsl}
                onChange={(e) =>
                  setSmtp((s) => ({ ...s, useSsl: e.target.checked }))
                }
                style={{ width: 16, height: 16 }}
              />
              <span style={{ fontWeight: 500 }}>
                Use implicit TLS (SSL){" "}
                <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>
                  — on for port 465, off for 587 (STARTTLS)
                </span>
              </span>
            </label>
          </div>

          <div className="field">
            <label>Username / Email</label>
            <input
              className="input"
              value={smtp.user}
              onChange={(e) =>
                setSmtp((s) => ({ ...s, user: e.target.value }))
              }
              placeholder="your-email@gmail.com"
            />
          </div>

          <div className="field">
            <label>App Password</label>
            <div style={{ position: "relative" }}>
              <input
                className="input"
                type={showPass ? "text" : "password"}
                value={smtp.pass}
                onChange={(e) =>
                  setSmtp((s) => ({ ...s, pass: e.target.value }))
                }
                placeholder={smtpHasPassword ? "Leave blank to keep current password" : "xxxx xxxx xxxx xxxx"}
                style={{ paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "var(--text-muted)",
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <Icon name={showPass ? "eye-off" : "eye"} size={16} />
              </button>
            </div>
            <small style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
              For Gmail: use an App Password (Google Account &rarr; Security &rarr;
              App passwords)
            </small>
          </div>

          <div className="field">
            <label>Sender Name</label>
            <input
              className="input"
              value={smtp.fromName}
              onChange={(e) =>
                setSmtp((s) => ({ ...s, fromName: e.target.value }))
              }
              placeholder="Daily Reports"
            />
          </div>

          <div className="field">
            <label>Sender Email</label>
            <input
              className="input"
              type="email"
              value={smtp.fromEmail}
              onChange={(e) =>
                setSmtp((s) => ({ ...s, fromEmail: e.target.value }))
              }
              placeholder="reports@mycompany.com"
            />
          </div>

          <div className="field">
            <label>App Name</label>
            <input
              className="input"
              value={smtp.appName}
              onChange={(e) =>
                setSmtp((s) => ({ ...s, appName: e.target.value }))
              }
              placeholder="Daily Reports Platform"
            />
            <small style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
              Used in email subjects and templates (e.g. &quot;Verify your [App Name] email&quot;)
            </small>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              className="btn btn-primary"
              onClick={saveSmtpConfig}
              disabled={smtpSaving}
            >
              <Icon name="check" size={16} />{" "}
              {smtpSaving ? "Saving…" : "Save configuration"}
            </button>
            <button
              className="btn btn-outline"
              onClick={sendTestEmail}
              disabled={smtpTesting || !smtpLoaded}
            >
              <Icon name="send" size={16} />{" "}
              {smtpTesting ? "Sending…" : "Send test email"}
            </button>
          </div>
        </div>
      )}

      {tabLabel === "Security" && (
        <div className="card">
          <h3 style={{ fontSize: 16, marginBottom: 18 }}>Password</h3>
          <div className="field">
            <label>Current password</label>
            <input
              type="password"
              className="input"
              value={pw.current}
              onChange={(e) =>
                setPw((p) => ({ ...p, current: e.target.value }))
              }
              placeholder="Enter current password"
            />
          </div>
          <div className="field">
            <label>New password</label>
            <input
              type="password"
              className="input"
              value={pw.next}
              onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
              placeholder="At least 8 characters"
            />
          </div>
          <div className="field">
            <label>Confirm new password</label>
            <input
              type="password"
              className="input"
              value={pw.confirm}
              onChange={(e) =>
                setPw((p) => ({ ...p, confirm: e.target.value }))
              }
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={submitPassword}
            disabled={pwSaving}
          >
            <Icon name="lock" size={16} />{" "}
            {pwSaving ? "Updating…" : "Change password"}
          </button>
        </div>
      )}

      {tabLabel === "Security" && <TwoFactorSettings />}
    </section>
  );
}
